/** Account Management store. Admin-only. Does not touch buyer/seller checklist state. */
import { DEMO_IDS, createUser } from "../db/schema";
import { ensureJourneyDb, insertRow, listTable, updateRow, writeAudit } from "../db";
import { setCredential } from "../db/credentials";
import { listLoginEvents } from "@hub";
import { defaultBrokerPlanId, normalizePlanId } from "@billing/catalog.js";
import {
  ACCOUNTS_EVENT,
  ACCOUNTS_PREFIX,
  ACCOUNT_ROLES,
  ACCOUNT_STATUSES,
  GRACE_DAYS,
  PAST_DUE_TO_GRACE_DAYS,
  PLANS,
  canManageAccounts,
  planById,
} from "./constants.js";

const KEYS = {
  accounts: `${ACCOUNTS_PREFIX}users`,
  orgs: `${ACCOUNTS_PREFIX}orgs`,
  subs: `${ACCOUNTS_PREFIX}subs`,
  billing: `${ACCOUNTS_PREFIX}billing`,
  invoices: `${ACCOUNTS_PREFIX}invoices`,
  receipts: `${ACCOUNTS_PREFIX}receipts`,
  payments: `${ACCOUNTS_PREFIX}payments`,
  adjustments: `${ACCOUNTS_PREFIX}adjustments`,
  reminders: `${ACCOUNTS_PREFIX}reminders`,
  audit: `${ACCOUNTS_PREFIX}audit`,
  seeded: `${ACCOUNTS_PREFIX}seeded`,
};

function read(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function write(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
  emitAccountsChange();
}

export function emitAccountsChange() {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(`${ACCOUNTS_PREFIX}revision`, String(Date.now()));
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new Event(ACCOUNTS_EVENT));
}

function nowIso() {
  return new Date().toISOString();
}

function newId() {
  return crypto.randomUUID();
}

function addDays(iso, days) {
  const date = new Date(iso || Date.now());
  date.setDate(date.getDate() + Number(days || 0));
  return date.toISOString();
}

function dayStamp(iso = nowIso()) {
  return String(iso).slice(0, 10);
}

export function assertAdmin(session) {
  if (!canManageAccounts(session)) {
    throw new Error("Account Management is for the program desk only.");
  }
}

function audit(session, action, metadata = {}) {
  const rows = read(KEYS.audit, []);
  rows.push({
    id: newId(),
    user_id: session?.userId || "admin-dev",
    action_type: action,
    timestamp: nowIso(),
    metadata,
  });
  write(KEYS.audit, rows.slice(-800));
  try {
    writeAudit({
      user_id: session?.userId || "admin-dev",
      action_type: action,
      transaction_id: null,
      metadata,
    });
  } catch {
    /* journey audit is best-effort */
  }
}

function lastLoginFor(userId) {
  try {
    const events = listLoginEvents(userId) || [];
    return events[0]?.at || events[0]?.created_at || "";
  } catch {
    return "";
  }
}

function defaultBilling({ name, city, county, state, ownerId, ownerKind }) {
  return {
    id: newId(),
    owner_id: ownerId,
    owner_kind: ownerKind,
    billing_name: name,
    address_line: "1 Harbor Walk",
    city: city || "Santa Barbara",
    county: county || "Santa Barbara",
    state: state || "CA",
    postal: "93101",
    tax_id: "",
    tax_jurisdiction: state || "CA",
    created_at: nowIso(),
  };
}

function defaultSub({ ownerId, ownerKind, planId, status, start, beta }) {
  const plan = planById(planId);
  return {
    id: newId(),
    owner_id: ownerId,
    owner_kind: ownerKind,
    plan_id: plan.id,
    amount: plan.amount,
    interval: plan.interval,
    start_date: start,
    renewal_date: addDays(start, 30),
    status,
    payment_method_ref: `pm_local_card_4242_${String(ownerId).slice(0, 6)}`,
    trial: Boolean(beta),
    beta: Boolean(beta),
    created_at: start,
  };
}

function seedPack() {
  const start = "2026-06-02T16:00:00.000Z";
  const coastal = {
    id: "org-coastal",
    name: "Coastal Realty",
    type: "brokerage",
    city: "Santa Barbara",
    county: "Santa Barbara",
    state: "CA",
    seat_limit: 20,
    created_at: start,
  };
  const harbor = {
    id: "org-harbor",
    name: "Harbor Desk Partners",
    type: "corporate",
    city: "San Luis Obispo",
    county: "San Luis Obispo",
    state: "CA",
    seat_limit: 0,
    created_at: "2026-07-10T16:00:00.000Z",
  };
  const accounts = [
    {
      id: DEMO_IDS.admin,
      name: "Dev Admin",
      email: "admin@dreamhome.local",
      phone: "805-555-0100",
      role: "admin",
      status: "active",
      organization_id: "",
      city: "Santa Barbara",
      county: "Santa Barbara",
      state: "CA",
      journey_user_id: DEMO_IDS.admin,
      created_at: start,
      last_login: lastLoginFor(DEMO_IDS.admin),
    },
    {
      id: DEMO_IDS.broker,
      name: "Morgan Ellison",
      email: "broker@dreamhome.local",
      phone: "805-555-0140",
      role: "broker",
      status: "active",
      organization_id: coastal.id,
      city: "Santa Barbara",
      county: "Santa Barbara",
      state: "CA",
      journey_user_id: DEMO_IDS.broker,
      created_at: start,
      last_login: lastLoginFor(DEMO_IDS.broker),
    },
    {
      id: DEMO_IDS.agent,
      name: "Jordan Hale",
      email: "agent@dreamhome.local",
      phone: "805-555-0142",
      role: "agent",
      status: "active",
      organization_id: coastal.id,
      city: "Santa Barbara",
      county: "Santa Barbara",
      state: "CA",
      journey_user_id: DEMO_IDS.agent,
      created_at: start,
      last_login: lastLoginFor(DEMO_IDS.agent),
    },
    {
      id: DEMO_IDS.agent2,
      name: "Sam Ortiz",
      email: "agent2@dreamhome.local",
      phone: "805-555-0144",
      role: "agent",
      status: "beta",
      organization_id: coastal.id,
      city: "Goleta",
      county: "Santa Barbara",
      state: "CA",
      journey_user_id: DEMO_IDS.agent2,
      created_at: "2026-07-18T16:00:00.000Z",
      last_login: lastLoginFor(DEMO_IDS.agent2),
    },
    {
      id: "acct-priya",
      name: "Priya Shah",
      email: "priya@coastal.example",
      phone: "805-555-0188",
      role: "agent",
      status: "active",
      organization_id: coastal.id,
      city: "Ventura",
      county: "Ventura",
      state: "CA",
      journey_user_id: "",
      created_at: "2026-08-04T16:00:00.000Z",
      last_login: "",
    },
    {
      id: "acct-lee",
      name: "Lee Navarro",
      email: "lee@harbor.example",
      phone: "805-555-0190",
      role: "corporate",
      status: "active",
      organization_id: harbor.id,
      city: "San Luis Obispo",
      county: "San Luis Obispo",
      state: "CA",
      journey_user_id: "",
      created_at: "2026-07-10T16:00:00.000Z",
      last_login: "",
    },
    {
      id: "acct-past",
      name: "Chris Bell",
      email: "chris@past.example",
      phone: "805-555-0192",
      role: "agent",
      status: "active",
      organization_id: "",
      city: "Oxnard",
      county: "Ventura",
      state: "CA",
      journey_user_id: "",
      created_at: "2026-05-12T16:00:00.000Z",
      last_login: "2026-08-01T12:00:00.000Z",
    },
  ];

  const billing = [
    defaultBilling({ name: "Coastal Realty", city: "Santa Barbara", county: "Santa Barbara", state: "CA", ownerId: coastal.id, ownerKind: "org" }),
    defaultBilling({ name: "Harbor Desk Partners", city: "San Luis Obispo", county: "San Luis Obispo", state: "CA", ownerId: harbor.id, ownerKind: "org" }),
    defaultBilling({ name: "Chris Bell", city: "Oxnard", county: "Ventura", state: "CA", ownerId: "acct-past", ownerKind: "account" }),
    defaultBilling({ name: "Dev Admin", city: "Santa Barbara", county: "Santa Barbara", state: "CA", ownerId: DEMO_IDS.admin, ownerKind: "account" }),
  ];

  const subs = [
    defaultSub({ ownerId: coastal.id, ownerKind: "org", planId: "broker_20", status: "active", start }),
    defaultSub({ ownerId: harbor.id, ownerKind: "org", planId: "corporate", status: "active", start: harbor.created_at }),
    defaultSub({ ownerId: DEMO_IDS.agent, ownerKind: "account", planId: "agent_monthly", status: "active", start }),
    defaultSub({ ownerId: DEMO_IDS.agent2, ownerKind: "account", planId: "beta", status: "active", start: "2026-07-18T16:00:00.000Z", beta: true }),
    defaultSub({ ownerId: "acct-priya", ownerKind: "account", planId: "agent_monthly", status: "active", start: "2026-08-04T16:00:00.000Z" }),
    {
      ...defaultSub({ ownerId: "acct-past", ownerKind: "account", planId: "agent_monthly", status: "past_due", start: "2026-05-12T16:00:00.000Z" }),
      renewal_date: "2026-08-12T16:00:00.000Z",
    },
  ];

  const invoices = [];
  const receipts = [];
  const payments = [];
  accounts
    .filter((row) => row.role !== "admin")
    .forEach((account, index) => {
      const sub = subs.find((row) => row.owner_id === account.id || row.owner_id === account.organization_id);
      if (!sub || !sub.amount) return;
      const paidAt = addDays(account.created_at, 1);
      const invoice = {
        id: `inv-seed-${index}`,
        number: `INV-2026-${String(1001 + index)}`,
        owner_id: sub.owner_id,
        owner_kind: sub.owner_kind,
        account_id: account.id,
        subscription_id: sub.id,
        amount: sub.amount,
        tax_jurisdiction: account.state,
        status: "paid",
        issued_at: paidAt,
        paid_at: paidAt,
      };
      invoices.push(invoice);
      payments.push({
        id: `pay-seed-${index}`,
        invoice_id: invoice.id,
        owner_id: sub.owner_id,
        account_id: account.id,
        amount: sub.amount,
        result: "succeeded",
        method_ref: sub.payment_method_ref,
        attempt: 1,
        note: "Opening month",
        at: paidAt,
      });
      receipts.push({
        id: `rcpt-seed-${index}`,
        invoice_id: invoice.id,
        number: `R-2026-${String(1001 + index)}`,
        amount: sub.amount,
        at: paidAt,
      });
    });

  payments.push({
    id: "pay-fail-chris",
    invoice_id: "",
    owner_id: "acct-past",
    account_id: "acct-past",
    amount: 29.99,
    result: "failed",
    method_ref: "pm_local_card_4242_past",
    attempt: 1,
    note: "Card declined — insufficient funds (gateway).",
    at: "2026-08-12T16:10:00.000Z",
  });

  const reminders = [
    {
      id: "dun-chris-1",
      account_id: "acct-past",
      kind: "failed_payment",
      tone: "gentle",
      body: "A quiet note: last month’s desk fee did not go through. You may update the card when it is convenient.",
      at: "2026-08-13T15:00:00.000Z",
      sent: true,
    },
  ];

  return {
    accounts,
    orgs: [coastal, harbor],
    subs,
    billing,
    invoices,
    receipts,
    payments,
    adjustments: [],
    reminders,
  };
}

function hydrateJourneySeats() {
  ensureJourneyDb();
  const have = new Set(read(KEYS.accounts, []).map((row) => row.email));
  const extras = listTable("users").filter(
    (row) => (row.role_type === "agent" || row.role_type === "broker") && row.email && !have.has(row.email)
  );
  if (!extras.length) return;
  const accounts = read(KEYS.accounts, []);
  extras.forEach((user) => {
    accounts.push({
      id: user.id,
      name: user.name || "",
      email: user.email,
      phone: user.phone || "",
      role: user.role_type,
      status: user.betaTester ? "beta" : user.status === "inactive" ? "suspended" : "active",
      organization_id: user.brokerage_id || "",
      city: "",
      county: "",
      state: "CA",
      journey_user_id: user.id,
      created_at: user.created_at || nowIso(),
      last_login: lastLoginFor(user.id),
    });
  });
  localStorage.setItem(KEYS.accounts, JSON.stringify(accounts));
}

export function ensureAccounts() {
  if (read(KEYS.seeded, false)) {
    hydrateJourneySeats();
    return true;
  }
  const pack = seedPack();
  write(KEYS.accounts, pack.accounts);
  write(KEYS.orgs, pack.orgs);
  write(KEYS.subs, pack.subs);
  write(KEYS.billing, pack.billing);
  write(KEYS.invoices, pack.invoices);
  write(KEYS.receipts, pack.receipts);
  write(KEYS.payments, pack.payments);
  write(KEYS.adjustments, pack.adjustments);
  write(KEYS.reminders, pack.reminders);
  write(KEYS.audit, []);
  write(KEYS.seeded, true);
  return true;
}

export function listAccounts() {
  ensureAccounts();
  return read(KEYS.accounts, []).sort((a, b) => String(a.name).localeCompare(String(b.name)));
}

export function getAccount(id) {
  return listAccounts().find((row) => row.id === id) || null;
}

export function listOrganizations() {
  ensureAccounts();
  return read(KEYS.orgs, []).sort((a, b) => String(a.name).localeCompare(String(b.name)));
}

export function getOrganization(id) {
  return listOrganizations().find((row) => row.id === id) || null;
}

export function listSubscriptions() {
  ensureAccounts();
  return read(KEYS.subs, []);
}

export function subscriptionFor(ownerId) {
  return listSubscriptions().find((row) => row.owner_id === ownerId && row.status !== "cancelled") ||
    listSubscriptions().find((row) => row.owner_id === ownerId) ||
    null;
}

export function listBillingProfiles() {
  ensureAccounts();
  return read(KEYS.billing, []);
}

export function billingFor(ownerId) {
  return listBillingProfiles().find((row) => row.owner_id === ownerId) || null;
}

export function listInvoices() {
  ensureAccounts();
  return read(KEYS.invoices, []).sort((a, b) => String(b.issued_at).localeCompare(String(a.issued_at)));
}

export function listReceipts() {
  ensureAccounts();
  return read(KEYS.receipts, []).sort((a, b) => String(b.at).localeCompare(String(a.at)));
}

export function listPayments() {
  ensureAccounts();
  return read(KEYS.payments, []).sort((a, b) => String(b.at).localeCompare(String(a.at)));
}

export function listAdjustments() {
  ensureAccounts();
  return read(KEYS.adjustments, []).sort((a, b) => String(b.at).localeCompare(String(a.at)));
}

export function listReminders() {
  ensureAccounts();
  return read(KEYS.reminders, []).sort((a, b) => String(b.at).localeCompare(String(a.at)));
}

export function listAccountAudit() {
  ensureAccounts();
  return read(KEYS.audit, []).sort((a, b) => String(b.timestamp).localeCompare(String(a.timestamp)));
}

function syncJourneyUser(account) {
  if (!account?.email) return account;
  if (account.role === "corporate") return account;
  ensureJourneyDb();
  const users = listTable("users");
  const existing = users.find((row) => row.id === account.journey_user_id || row.email === account.email);
  const roleType = account.role === "admin" ? "broker" : account.role === "agent" || account.role === "broker" ? account.role : null;
  if (!roleType) return account;
  const org = account.organization_id ? getOrganization(account.organization_id) : null;
  const brokerId =
    org?.type === "brokerage"
      ? listAccounts().find((row) => row.organization_id === org.id && row.role === "broker")?.journey_user_id ||
        listAccounts().find((row) => row.organization_id === org.id && row.role === "broker")?.id ||
        null
      : null;
  const payload = {
    name: account.name,
    email: account.email,
    phone: account.phone || "",
    role_type: roleType,
    status: account.status === "suspended" || account.status === "cancelled" ? "inactive" : "active",
    brokerage_id: account.role === "agent" ? brokerId : null,
    office_name: org?.name || "",
    plan: subscriptionFor(account.id)?.plan_id || null,
    plan_status: subscriptionFor(account.id)?.status || null,
    betaTester: account.status === "beta",
  };
  if (existing) {
    updateRow("users", existing.id, payload);
    return { ...account, journey_user_id: existing.id };
  }
  if (account.role === "admin") return account;
  const user = createUser({ ...payload, id: account.id });
  insertRow("users", user);
  setCredential(user.id, "demo123");
  return { ...account, journey_user_id: user.id };
}

function saveAccount(row) {
  const rows = listAccounts();
  const index = rows.findIndex((item) => item.id === row.id);
  if (index === -1) rows.push(row);
  else rows[index] = row;
  write(KEYS.accounts, rows);
  return row;
}

export function createAccount(session, input) {
  assertAdmin(session);
  const email = String(input.email || "").trim().toLowerCase();
  if (!email || !String(input.name || "").trim()) throw new Error("A name and email are needed.");
  if (listAccounts().some((row) => row.email === email)) throw new Error("That email already has an account.");
  const role = ACCOUNT_ROLES.some((row) => row.id === input.role) ? input.role : "agent";
  const status = ACCOUNT_STATUSES.some((row) => row.id === input.status) ? input.status : "active";
  let row = {
    id: newId(),
    name: String(input.name || "").trim(),
    email,
    phone: String(input.phone || "").trim(),
    role,
    status,
    organization_id: input.organization_id || "",
    city: String(input.city || "").trim(),
    county: String(input.county || "").trim(),
    state: String(input.state || "CA").trim(),
    journey_user_id: "",
    created_at: nowIso(),
    last_login: "",
  };
  row = syncJourneyUser(row);
  saveAccount(row);
  const planId =
    input.plan_id ||
    (status === "beta"
      ? "beta"
      : role === "broker"
        ? defaultBrokerPlanId()
        : role === "corporate"
          ? "corporate"
          : "agent_monthly");
  const sub = defaultSub({
    ownerId: row.id,
    ownerKind: "account",
    planId,
    status: status === "cancelled" ? "cancelled" : status === "suspended" ? "suspended" : "active",
    start: row.created_at,
    beta: status === "beta",
  });
  write(KEYS.subs, [...listSubscriptions(), sub]);
  write(KEYS.billing, [
    ...listBillingProfiles(),
    defaultBilling({
      name: row.name,
      city: row.city,
      county: row.county,
      state: row.state,
      ownerId: row.id,
      ownerKind: "account",
    }),
  ]);
  audit(session, "account.create", { account_id: row.id, email: row.email, role: row.role });
  return row;
}

export function updateAccount(session, id, patch) {
  assertAdmin(session);
  const current = getAccount(id);
  if (!current) throw new Error("Account not found.");
  if (patch.email && listAccounts().some((row) => row.email === String(patch.email).toLowerCase() && row.id !== id)) {
    throw new Error("That email already has an account.");
  }
  let next = {
    ...current,
    ...patch,
    id,
    email: patch.email != null ? String(patch.email).toLowerCase() : current.email,
  };
  next = syncJourneyUser(next);
  saveAccount(next);
  audit(session, "account.update", { account_id: id, fields: Object.keys(patch) });
  return next;
}

export function setAccountStatus(session, id, status) {
  assertAdmin(session);
  if (!ACCOUNT_STATUSES.some((row) => row.id === status)) throw new Error("Unknown account status.");
  const current = getAccount(id);
  if (status === "suspended" && current?.status !== "suspended") {
    freezeBillingForUser(id, { reason: "Admin status change" });
  }
  if (status === "active" && current?.status === "suspended") {
    resumeBillingForUser(id, { fromPayment: false });
  }
  if (status === "cancelled") {
    freezeBillingForUser(id, { reason: "Account cancelled" });
  }
  const account = updateAccount(session, id, { status });
  return account;
}

export function changeAccountRole(session, id, role) {
  assertAdmin(session);
  if (!ACCOUNT_ROLES.some((row) => row.id === role)) throw new Error("Unknown role.");
  return updateAccount(session, id, { role });
}

export function assignAccountOrg(session, id, organizationId) {
  assertAdmin(session);
  if (organizationId && !getOrganization(organizationId)) throw new Error("Organization not found.");
  return updateAccount(session, id, { organization_id: organizationId || "" });
}

export function createOrganization(session, input) {
  assertAdmin(session);
  const name = String(input.name || "").trim();
  if (!name) throw new Error("Give the organization a name.");
  const row = {
    id: newId(),
    name,
    type: input.type === "corporate" ? "corporate" : "brokerage",
    city: String(input.city || "").trim(),
    county: String(input.county || "").trim(),
    state: String(input.state || "CA").trim(),
    seat_limit: Number(input.seat_limit || 0),
    created_at: nowIso(),
  };
  write(KEYS.orgs, [...listOrganizations(), row]);
  write(KEYS.billing, [
    ...listBillingProfiles(),
    defaultBilling({ name: row.name, city: row.city, county: row.county, state: row.state, ownerId: row.id, ownerKind: "org" }),
  ]);
  const planId = row.type === "corporate" ? "corporate" : defaultBrokerPlanId();
  write(KEYS.subs, [
    ...listSubscriptions(),
    defaultSub({ ownerId: row.id, ownerKind: "org", planId, status: "active", start: row.created_at }),
  ]);
  audit(session, "org.create", { organization_id: row.id, name: row.name });
  return row;
}

export function updateOrganization(session, id, patch) {
  assertAdmin(session);
  const rows = listOrganizations();
  const index = rows.findIndex((row) => row.id === id);
  if (index === -1) throw new Error("Organization not found.");
  rows[index] = { ...rows[index], ...patch, id };
  write(KEYS.orgs, rows);
  audit(session, "org.update", { organization_id: id });
  return rows[index];
}

export function updateBillingProfile(session, ownerId, patch) {
  assertAdmin(session);
  const rows = listBillingProfiles();
  const index = rows.findIndex((row) => row.owner_id === ownerId);
  if (index === -1) {
    const created = { ...defaultBilling({ name: patch.billing_name || "Billing", ownerId, ownerKind: patch.owner_kind || "account" }), ...patch };
    write(KEYS.billing, [...rows, created]);
    return created;
  }
  rows[index] = { ...rows[index], ...patch, owner_id: ownerId };
  write(KEYS.billing, rows);
  audit(session, "billing.update", { owner_id: ownerId });
  return rows[index];
}

function patchSubscription(session, id, patch) {
  const rows = listSubscriptions();
  const index = rows.findIndex((row) => row.id === id);
  if (index === -1) throw new Error("Subscription not found.");
  rows[index] = { ...rows[index], ...patch, id };
  write(KEYS.subs, rows);
  return rows[index];
}

export function changePlan(session, ownerId, planId) {
  assertAdmin(session);
  const plan = planById(planId);
  const sub = subscriptionFor(ownerId);
  if (!sub) throw new Error("No subscription on this record.");
  const next = patchSubscription(session, sub.id, { plan_id: plan.id, amount: plan.amount, interval: plan.interval });
  audit(session, "subscription.plan_change", { owner_id: ownerId, plan_id: plan.id });
  return next;
}

export function setPaymentMethod(session, ownerId, reference) {
  assertAdmin(session);
  const sub = subscriptionFor(ownerId);
  if (!sub) throw new Error("No subscription on this record.");
  const ref = String(reference || "").trim() || `pm_local_${newId().slice(0, 8)}`;
  const next = patchSubscription(session, sub.id, { payment_method_ref: ref });
  audit(session, "subscription.payment_method", { owner_id: ownerId, payment_method_ref: ref });
  return next;
}

function nextInvoiceNumber() {
  const count = listInvoices().length + 1;
  return `INV-${new Date().getFullYear()}-${String(1000 + count)}`;
}

function addInvoice(partial) {
  const row = {
    id: newId(),
    number: nextInvoiceNumber(),
    status: "open",
    issued_at: nowIso(),
    paid_at: "",
    ...partial,
  };
  write(KEYS.invoices, [row, ...listInvoices()]);
  return row;
}

function addReceipt(invoice) {
  const row = {
    id: newId(),
    invoice_id: invoice.id,
    number: `R-${invoice.number.replace("INV-", "")}`,
    amount: invoice.amount,
    at: nowIso(),
  };
  write(KEYS.receipts, [row, ...listReceipts()]);
  return row;
}

function addPayment(partial) {
  const row = {
    id: newId(),
    attempt: 1,
    at: nowIso(),
    ...partial,
  };
  write(KEYS.payments, [row, ...listPayments()]);
  return row;
}

function addReminder(partial) {
  const row = {
    id: newId(),
    tone: "gentle",
    sent: true,
    at: nowIso(),
    ...partial,
  };
  write(KEYS.reminders, [row, ...listReminders()]);
  return row;
}

function planIdForDeskSession(session = {}) {
  if (session.betaTester || session.plan === "beta") return "beta";
  if (session.plan || session.seat_tier) return normalizePlanId(session.plan, session.seat_tier);
  if (session.role === "corporate") return "corporate";
  if (session.role === "broker") return defaultBrokerPlanId();
  if (session.role === "agent") return "agent_monthly";
  return "agent_monthly";
}

function findDeskAccount(session = {}) {
  if (!session?.userId && !session?.email) return null;
  ensureAccounts();
  return (
    getAccount(session.userId) ||
    listAccounts().find((row) => row.journey_user_id === session.userId) ||
    listAccounts().find((row) => session.email && row.email === String(session.email).toLowerCase()) ||
    null
  );
}

/** Ensure Account Management has a row + subscription for a signed-in professional. */
export function ensureDeskBillingRecord(session = {}) {
  if (!session?.userId) return null;
  const role =
    session.role === "broker" || session.role === "agent" || session.role === "corporate"
      ? session.role
      : null;
  if (!role) return null;

  let account = findDeskAccount(session);
  if (!account) {
    account = {
      id: session.userId,
      name: String(session.name || "").trim() || "Desk user",
      email: String(session.email || "").trim().toLowerCase(),
      phone: String(session.phone || "").trim(),
      role,
      status: session.betaTester ? "beta" : "active",
      organization_id: "",
      city: "",
      county: "",
      state: "CA",
      journey_user_id: session.userId,
      created_at: nowIso(),
      last_login: nowIso(),
    };
    saveAccount(account);
    const planId = planIdForDeskSession(session);
    const sub = defaultSub({
      ownerId: account.id,
      ownerKind: "account",
      planId,
      status: session.plan_status === "signup" ? "signup" : session.betaTester ? "active" : "active",
      start: account.created_at,
      beta: Boolean(session.betaTester),
    });
    write(KEYS.subs, [...listSubscriptions(), sub]);
    write(KEYS.billing, [
      ...listBillingProfiles(),
      defaultBilling({
        name: account.name,
        city: account.city,
        county: account.county,
        state: account.state,
        ownerId: account.id,
        ownerKind: "account",
      }),
    ]);
    audit(session, "billing.ensure", { account_id: account.id, plan_id: planId });
  }

  let sub = subscriptionFor(account.id);
  if (!sub) {
    const planId = planIdForDeskSession(session);
    sub = defaultSub({
      ownerId: account.id,
      ownerKind: "account",
      planId,
      status: session.plan_status === "signup" ? "signup" : "active",
      start: account.created_at,
      beta: Boolean(session.betaTester),
    });
    write(KEYS.subs, [...listSubscriptions(), sub]);
  }

  return { account, subscription: sub };
}

function applyCheckoutPayment({ orderId, methodPrefix, payload = {}, session = null, note = "" }) {
  ensureAccounts();
  const id = String(orderId || "").trim();
  if (!id) return null;
  const methodRef = `${methodPrefix}:${id}`;
  if (listPayments().some((row) => row.method_ref === methodRef)) {
    return { duplicate: true, orderId: id };
  }

  const deskSession =
    session ||
    (payload.userId
      ? {
          userId: payload.userId,
          email: payload.payerEmail || "",
          role: payload.role || "agent",
          plan: payload.planId || "",
        }
      : null);
  const ensured = deskSession ? ensureDeskBillingRecord(deskSession) : null;
  const account =
    ensured?.account ||
    getAccount(payload.userId) ||
    listAccounts().find((row) => payload.payerEmail && row.email === String(payload.payerEmail).toLowerCase()) ||
    null;
  const ownerId = account?.id || payload.userId || "";
  let sub = ensured?.subscription || subscriptionFor(ownerId);
  if (!sub && ownerId) {
    sub = defaultSub({
      ownerId,
      ownerKind: "account",
      planId: normalizePlanId(payload.planId, payload.tier) || planIdForDeskSession(deskSession || {}),
      status: "active",
      start: nowIso(),
    });
    write(KEYS.subs, [...listSubscriptions(), sub]);
  }
  if (sub && payload.planId) {
    const plan = planById(normalizePlanId(payload.planId, payload.tier));
    sub = patchSubscription(null, sub.id, {
      plan_id: plan.id,
      amount: plan.amount,
      interval: plan.interval,
      status: "active",
      cancel_at_period_end: false,
      cancel_effective_at: "",
      cancel_requested_at: "",
    });
  } else if (sub) {
    sub = patchSubscription(null, sub.id, {
      status: "active",
      cancel_at_period_end: false,
      cancel_effective_at: "",
      cancel_requested_at: "",
    });
  }
  const amount = Number(payload.amount || sub?.amount || 0);
  const invoice = addInvoice({
    owner_id: sub?.owner_id || ownerId,
    owner_kind: sub?.owner_kind || "account",
    account_id: account?.id || "",
    subscription_id: sub?.id || "",
    amount,
    tax_jurisdiction: billingFor(sub?.owner_id || ownerId)?.tax_jurisdiction || account?.state || "CA",
    status: "paid",
    paid_at: nowIso(),
  });
  const payment = addPayment({
    invoice_id: invoice.id,
    owner_id: sub?.owner_id || ownerId,
    account_id: account?.id || "",
    amount,
    result: "succeeded",
    method_ref: methodRef,
    attempt: 1,
    note: note || (payload.planId ? `PayPal · ${payload.planId}` : "Checkout"),
  });
  addReceipt(invoice);
  if (sub) {
    patchSubscription(null, sub.id, {
      status: "active",
      payment_method_ref: methodRef,
      renewal_date: addDays(nowIso(), sub.interval === "year" ? 365 : 30),
    });
  }
  if (account && (account.status === "suspended" || account.status === "cancelled")) {
    const next = saveAccount({ ...account, status: "active" });
    syncJourneyUser(next);
  } else if (account) {
    syncJourneyUser(account);
  }
  audit({ userId: ownerId || methodPrefix }, `payment.${methodPrefix}`, {
    order_id: id,
    account_id: account?.id || "",
  });
  return { orderId: id, payment, invoice, account, subscription: sub };
}

export function applyPaypalCheckout(payload = {}, session = null) {
  return applyCheckoutPayment({
    orderId: payload.orderId,
    methodPrefix: "paypal",
    payload,
    session,
    note: payload.planId ? `PayPal · ${payload.planId}` : "PayPal checkout",
  });
}

export function applySandboxCheckout(session, payload = {}) {
  const orderId = payload.orderId || `sandbox_${newId().slice(0, 8)}`;
  return applyCheckoutPayment({
    orderId,
    methodPrefix: "sandbox",
    payload: {
      userId: session?.userId,
      payerEmail: session?.email,
      role: session?.role,
      planId: payload.planId || planIdForDeskSession(session),
      tier: session?.seat_tier,
      amount: payload.amount,
    },
    session,
    note: "Sandbox checkout (no card charged)",
  });
}

export const CANCEL_NOTICE_HOURS = 72;

export function scheduleSubscriptionCancel(session, { reason = "" } = {}) {
  const bundle = ensureDeskBillingRecord(session);
  if (!bundle?.subscription) throw new Error("No subscription on file.");
  const sub = bundle.subscription;
  if (sub.status === "cancelled") throw new Error("Subscription is already cancelled.");
  const effective = sub.renewal_date || addDays(nowIso(), 30);
  const next = patchSubscription(session, sub.id, {
    cancel_at_period_end: true,
    cancel_requested_at: nowIso(),
    cancel_effective_at: effective,
    cancel_reason: String(reason || "").trim(),
  });
  audit(session, "subscription.cancel_scheduled", {
    owner_id: sub.owner_id,
    effective_at: effective,
    notice_hours: CANCEL_NOTICE_HOURS,
  });
  return next;
}

export function revokeSubscriptionCancel(session) {
  const bundle = ensureDeskBillingRecord(session);
  if (!bundle?.subscription) throw new Error("No subscription on file.");
  const sub = bundle.subscription;
  const next = patchSubscription(session, sub.id, {
    cancel_at_period_end: false,
    cancel_requested_at: "",
    cancel_effective_at: "",
    cancel_reason: "",
  });
  audit(session, "subscription.cancel_revoked", { owner_id: sub.owner_id });
  return next;
}

function renewSubscriptionForOwner(session, ownerId, { note = "Desk renewal", force = false } = {}) {
  const sub = subscriptionFor(ownerId);
  if (!sub) throw new Error("No subscription on file.");
  if (sub.billing_frozen && !force) {
    return { skipped: true, reason: "billing_frozen", subscription: sub };
  }
  if (sub.cancel_at_period_end) {
    const next = patchSubscription(session, sub.id, {
      status: "cancelled",
      cancel_at_period_end: false,
      cancelled_at: nowIso(),
    });
    audit(session, "subscription.cancelled", { owner_id: ownerId, at_period_end: true });
    return { cancelled: true, subscription: next };
  }
  const invoice = addInvoice({
    owner_id: ownerId,
    owner_kind: sub.owner_kind,
    subscription_id: sub.id,
    amount: sub.amount,
    tax_jurisdiction: billingFor(ownerId)?.tax_jurisdiction || "CA",
    status: "paid",
    paid_at: nowIso(),
  });
  const payment = addPayment({
    invoice_id: invoice.id,
    owner_id: ownerId,
    account_id: sub.owner_kind === "account" ? ownerId : "",
    amount: sub.amount,
    result: "succeeded",
    method_ref: sub.payment_method_ref || `pm_renew_${newId().slice(0, 8)}`,
    attempt: 1,
    note,
  });
  addReceipt(invoice);
  const next = patchSubscription(session, sub.id, {
    status: "active",
    renewal_date: addDays(sub.renewal_date || nowIso(), sub.interval === "year" ? 365 : 30),
  });
  audit(session, "subscription.renewed", { owner_id: ownerId, invoice_id: invoice.id });
  return { renewed: true, subscription: next, invoice, payment };
}

export function processDeskRenewal(session, { force = false } = {}) {
  const bundle = ensureDeskBillingRecord(session);
  if (!bundle?.subscription) throw new Error("No subscription on file.");
  const sub = bundle.subscription;
  if (sub.status === "cancelled" && !sub.cancel_at_period_end) {
    throw new Error("Subscription is cancelled.");
  }
  const renewalDue = Date.parse(sub.renewal_date || sub.created_at || nowIso());
  if (!force && renewalDue > Date.now()) {
    return { skipped: true, reason: "not_due", renewal_date: sub.renewal_date, subscription: sub };
  }
  return renewSubscriptionForOwner(session, sub.owner_id, {
    note: force ? "Sandbox renewal (simulated)" : "Renewal",
  });
}

export function deskBillingBundle(session) {
  const bundle = ensureDeskBillingRecord(session);
  if (!bundle?.account) return null;
  return accountBundle(bundle.account.id);
}

export function freezeBillingForUser(ownerId, { reason = "" } = {}) {
  if (!ownerId) return null;
  ensureAccounts();
  const sub = subscriptionFor(ownerId);
  if (!sub) return null;
  const anchor = sub.billing_anchor_date || sub.renewal_date || nowIso();
  return patchSubscription(null, sub.id, {
    status: "suspended",
    billing_frozen: true,
    billing_frozen_at: nowIso(),
    billing_anchor_date: anchor,
    billing_frozen_reason: String(reason || "").trim(),
  });
}

export function resumeBillingForUser(ownerId, { fromPayment = false } = {}) {
  if (!ownerId) return null;
  ensureAccounts();
  const sub = subscriptionFor(ownerId);
  if (!sub) return null;
  const anchor = sub.billing_anchor_date || sub.renewal_date;
  const patch = {
    status: "active",
    billing_frozen: false,
    billing_frozen_at: "",
    billing_frozen_reason: "",
    billing_resumed_at: nowIso(),
  };
  if (fromPayment) {
    patch.renewal_date = addDays(nowIso(), sub.interval === "year" ? 365 : 30);
    patch.billing_anchor_date = patch.renewal_date;
  } else if (anchor && Date.parse(anchor) > Date.now()) {
    patch.renewal_date = anchor;
  }
  return patchSubscription(null, sub.id, patch);
}

export function chargeOwner(session, ownerId, { force = "auto", note = "" } = {}) {
  assertAdmin(session);
  const sub = subscriptionFor(ownerId);
  if (!sub) throw new Error("No subscription to charge.");
  if (!sub.amount) {
    audit(session, "payment.skip", { owner_id: ownerId, reason: "comped" });
    return { result: "succeeded", skipped: true };
  }
  const priorFails = listPayments().filter((row) => row.owner_id === ownerId && row.result === "failed").length;
  const succeed = force === "succeed" || (force !== "fail" && priorFails < 2);
  const invoice = addInvoice({
    owner_id: ownerId,
    owner_kind: sub.owner_kind,
    subscription_id: sub.id,
    amount: sub.amount,
    tax_jurisdiction: billingFor(ownerId)?.tax_jurisdiction || "CA",
    status: succeed ? "paid" : "open",
    paid_at: succeed ? nowIso() : "",
  });
  const payment = addPayment({
    invoice_id: invoice.id,
    owner_id: ownerId,
    account_id: sub.owner_kind === "account" ? ownerId : "",
    amount: sub.amount,
    result: succeed ? "succeeded" : "failed",
    method_ref: sub.payment_method_ref,
    attempt: priorFails + 1,
    note: note || (succeed ? "Manual renewal" : "Gateway declined"),
  });
  if (succeed) {
    addReceipt(invoice);
    patchSubscription(session, sub.id, { status: "active", renewal_date: addDays(nowIso(), 30) });
    const account = getAccount(ownerId);
    if (account && account.status === "suspended") updateAccount(session, ownerId, { status: "active" });
  } else {
    patchSubscription(session, sub.id, { status: "past_due" });
    addReminder({
      account_id: sub.owner_kind === "account" ? ownerId : "",
      owner_id: ownerId,
      kind: "failed_payment",
      body: "A quiet note: this month’s desk fee did not go through. You may update the card when it is convenient. There is a short grace window before the desk pauses.",
    });
  }
  audit(session, succeed ? "payment.succeeded" : "payment.failed", { owner_id: ownerId, invoice_id: invoice.id });
  return { result: payment.result, invoice, payment };
}

export function issueCredit(session, ownerId, { amount, note, compMonth = false }) {
  assertAdmin(session);
  const value = Number(amount || 0);
  if (!compMonth && !value) throw new Error("Enter a credit amount or comp a month.");
  const sub = subscriptionFor(ownerId);
  if (compMonth && sub) {
    patchSubscription(session, sub.id, { renewal_date: addDays(sub.renewal_date || nowIso(), 30) });
  }
  const row = {
    id: newId(),
    owner_id: ownerId,
    kind: compMonth ? "comp_month" : "credit",
    amount: compMonth ? sub?.amount || 0 : value,
    note: String(note || "").trim() || (compMonth ? "One month comped." : "Manual credit"),
    at: nowIso(),
    by: session.userId,
  };
  write(KEYS.adjustments, [row, ...listAdjustments()]);
  audit(session, "billing.credit", { owner_id: ownerId, kind: row.kind, amount: row.amount });
  return row;
}

export function markRefund(session, paymentId, note) {
  assertAdmin(session);
  const rows = listPayments();
  const index = rows.findIndex((row) => row.id === paymentId);
  if (index === -1) throw new Error("Payment not found.");
  if (rows[index].result !== "succeeded") throw new Error("Only a successful payment can be marked refunded.");
  rows[index] = { ...rows[index], result: "refunded", refund_note: String(note || "").trim(), refunded_at: nowIso() };
  write(KEYS.payments, rows);
  const invoices = listInvoices().map((row) => (row.id === rows[index].invoice_id ? { ...row, status: "refunded" } : row));
  write(KEYS.invoices, invoices);
  audit(session, "payment.refund", { payment_id: paymentId, note });
  return rows[index];
}

export function overrideSuspension(session, accountId, note) {
  assertAdmin(session);
  resumeBillingForUser(accountId, { fromPayment: false });
  const account = setAccountStatus(session, accountId, "active");
  audit(session, "account.override_suspension", { account_id: accountId, note: String(note || "").trim() });
  return account;
}

export function deleteAccount(session, accountId, { confirmEmail = "" } = {}) {
  assertAdmin(session);
  const account = getAccount(accountId);
  if (!account) throw new Error("Account not found.");
  if (account.role === "admin") throw new Error("The program admin account cannot be deleted here.");
  const email = String(confirmEmail || "").trim().toLowerCase();
  if (!email || email !== String(account.email || "").toLowerCase()) {
    throw new Error("Type the account email to confirm deletion.");
  }
  const sub = subscriptionFor(accountId);
  if (sub) {
    patchSubscription(session, sub.id, {
      status: "cancelled",
      cancel_at_period_end: false,
      cancelled_at: nowIso(),
    });
  }
  freezeBillingForUser(accountId, { reason: "Account deleted" });
  const next = saveAccount({ ...account, status: "deleted", deleted_at: nowIso() });
  syncJourneyUser({ ...next, status: "cancelled" });
  audit(session, "account.delete", { account_id: accountId, email: account.email });
  return next;
}

export function runDunning(session) {
  assertAdmin(session);
  const now = Date.now();
  let moved = 0;
  listSubscriptions().forEach((sub) => {
    if (sub.status === "past_due") {
      const failed = listPayments().find((row) => row.owner_id === sub.owner_id && row.result === "failed");
      const age = failed ? (now - Date.parse(failed.at)) / 86400000 : 99;
      if (age >= PAST_DUE_TO_GRACE_DAYS) {
        patchSubscription(session, sub.id, { status: "grace" });
        addReminder({
          owner_id: sub.owner_id,
          account_id: sub.owner_kind === "account" ? sub.owner_id : "",
          kind: "grace",
          body: "A second quiet note: the desk is in a short grace window. Update the card to keep the hall open.",
        });
        moved += 1;
      }
    } else if (sub.status === "grace") {
      const started = Date.parse(sub.renewal_date || sub.created_at);
      if ((now - started) / 86400000 >= GRACE_DAYS) {
        patchSubscription(session, sub.id, { status: "suspended" });
        if (sub.owner_kind === "account") {
          const account = getAccount(sub.owner_id);
          if (account && account.status === "active") updateAccount(session, sub.owner_id, { status: "suspended" });
        }
        addReminder({
          owner_id: sub.owner_id,
          account_id: sub.owner_kind === "account" ? sub.owner_id : "",
          kind: "suspend",
          body: "The desk is paused after the grace window. Write to the program desk when you are ready to reopen.",
        });
        moved += 1;
      }
    }
  });
  audit(session, "dunning.run", { moved });
  return { moved };
}

export function seatsForOrg(orgId) {
  const org = getOrganization(orgId);
  const members = listAccounts().filter((row) => row.organization_id === orgId && row.status !== "cancelled");
  const agents = members.filter((row) => row.role === "agent").length;
  const limit = org?.seat_limit || planById(subscriptionFor(orgId)?.plan_id)?.seats || 0;
  return { members: members.length, agents, limit, remaining: limit ? Math.max(0, limit - agents) : null };
}

export function accountBundle(id) {
  const account = getAccount(id);
  if (!account) return null;
  const org = account.organization_id ? getOrganization(account.organization_id) : null;
  const ownerIds = [account.id, account.organization_id].filter(Boolean);
  return {
    account,
    org,
    subscription: subscriptionFor(account.id) || (org ? subscriptionFor(org.id) : null),
    billing: billingFor(account.id) || (org ? billingFor(org.id) : null),
    invoices: listInvoices().filter((row) => ownerIds.includes(row.owner_id) || row.account_id === id),
    receipts: listReceipts().filter((row) => ownerIds.includes(row.invoice_id) || listInvoices().some((inv) => inv.id === row.invoice_id && (ownerIds.includes(inv.owner_id) || inv.account_id === id))),
    payments: listPayments().filter((row) => ownerIds.includes(row.owner_id) || row.account_id === id),
    adjustments: listAdjustments().filter((row) => ownerIds.includes(row.owner_id)),
    reminders: listReminders().filter((row) => row.account_id === id || ownerIds.includes(row.owner_id)),
    seats: org ? seatsForOrg(org.id) : null,
  };
}

export { PLANS, dayStamp };
